/* ATTCU hero — raw WebGL ink field. One quad, one hand-written fragment shader.
   IKB turbulence + halftone lattice, pointer-reactive. No library. */
(() => {
  'use strict';
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const canvas = document.querySelector('.hero-canvas');
  if (!canvas) return;
  const gl = canvas.getContext('webgl', { antialias: false, alpha: false, powerPreference: 'low-power' });
  if (!gl) return;

  const VERT = `
attribute vec2 p;
void main(){ gl_Position = vec4(p, 0., 1.); }`;

  const FRAG = `
precision mediump float;
uniform vec2 u_res;
uniform float u_time;
uniform vec2 u_ptr;    /* 0..1, y up */
uniform float u_ptrv;  /* pointer energy 0..1 */

/* riso ink drawer */
const vec3 IKB   = vec3(0.000, 0.184, 0.655);
const vec3 DEEP  = vec3(0.000, 0.106, 0.400);
const vec3 LIVE  = vec3(0.114, 0.286, 0.898);
const vec3 PAPER = vec3(0.957, 0.937, 0.890);
const vec3 FLUO  = vec3(1.000, 0.282, 0.706);

float hash(vec2 q){ return fract(sin(dot(q, vec2(127.1, 311.7))) * 43758.5453); }
float noise(vec2 q){
  vec2 i = floor(q), f = fract(q);
  vec2 u = f * f * (3. - 2. * f);
  return mix(mix(hash(i), hash(i + vec2(1., 0.)), u.x),
             mix(hash(i + vec2(0., 1.)), hash(i + vec2(1., 1.)), u.x), u.y);
}
float fbm(vec2 q){
  float v = 0., a = .5;
  for (int i = 0; i < 4; i++){ v += a * noise(q); q = q * 2.03 + 17.1; a *= .5; }
  return v;
}

void main(){
  vec2 uv = gl_FragCoord.xy / u_res;
  vec2 asp = vec2(u_res.x / u_res.y, 1.);
  vec2 q = uv * asp;

  /* slow ink weather */
  float t = u_time * .05;
  float field = fbm(q * 2.1 + vec2(t, -t * .6) + fbm(q * 3.7 - t) * .55);

  /* pointer displaces the ink like a thumb on a wet print */
  vec2 pq = u_ptr * asp;
  float d = distance(q, pq);
  float press = exp(-d * 5.5) * (0.35 + u_ptrv);
  field += press * .5;

  /* base pool: deep -> ikb -> live streaks */
  vec3 col = mix(DEEP, IKB, smoothstep(.25, .62, field));
  col = mix(col, LIVE, smoothstep(.72, .95, field) * .8);

  /* halftone lattice riding the field */
  float s = 140.;
  vec2 g = mat2(.9659, .2588, -.2588, .9659) * (q * s);   /* 15deg screen angle */
  vec2 cell = fract(g) - .5;
  float rad = .42 * smoothstep(.15, .95, field + press * .8);
  float dot_ = smoothstep(rad, rad - .12, length(cell));
  col = mix(col, mix(IKB, PAPER, .18 + press), dot_ * .35);

  /* roller bands: horizontal ink unevenness */
  col *= 1. - .05 * sin(uv.y * 60. + fbm(vec2(uv.y * 8., t)) * 4.);

  /* rare fluoro tick: a thin scanline of the second ink */
  float tick = step(.9965, hash(vec2(floor(u_time * 2.), floor(uv.y * 90.))));
  col = mix(col, FLUO, tick * .5);

  /* paper flecks in the ink */
  float fleck = step(.988, hash(q * 240. + floor(u_time * 3.)));
  col = mix(col, PAPER, fleck * .22);

  /* edge vignette: ink starving at the sheet edge */
  float vig = smoothstep(0., .18, uv.x) * smoothstep(1., .82, uv.x)
            * smoothstep(0., .14, uv.y) * smoothstep(1., .86, uv.y);
  col = mix(col * .82, col, vig);

  gl_FragColor = vec4(col, 1.);
}`;

  function sh(type, src) {
    const s = gl.createShader(type);
    gl.shaderSource(s, src); gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) { console.warn(gl.getShaderInfoLog(s)); return null; }
    return s;
  }
  const prog = gl.createProgram();
  const v = sh(gl.VERTEX_SHADER, VERT), f = sh(gl.FRAGMENT_SHADER, FRAG);
  if (!v || !f) return;
  gl.attachShader(prog, v); gl.attachShader(prog, f); gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return;
  gl.useProgram(prog);

  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
  const loc = gl.getAttribLocation(prog, 'p');
  gl.enableVertexAttribArray(loc);
  gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

  const uRes = gl.getUniformLocation(prog, 'u_res');
  const uTime = gl.getUniformLocation(prog, 'u_time');
  const uPtr = gl.getUniformLocation(prog, 'u_ptr');
  const uPtrV = gl.getUniformLocation(prog, 'u_ptrv');

  const dpr = Math.min(devicePixelRatio || 1, 1.5);
  function size() {
    const w = canvas.clientWidth, h = canvas.clientHeight;
    if (canvas.width !== (w * dpr | 0) || canvas.height !== (h * dpr | 0)) {
      canvas.width = w * dpr | 0; canvas.height = h * dpr | 0;
      gl.viewport(0, 0, canvas.width, canvas.height);
    }
  }

  let px = 0.5, py = 0.4, tx = 0.5, ty = 0.4, energy = 0;
  addEventListener('pointermove', (e) => {
    const r = canvas.getBoundingClientRect();
    if (r.bottom < 0) return;
    tx = (e.clientX - r.left) / r.width;
    ty = 1 - (e.clientY - r.top) / r.height;
    energy = Math.min(energy + 0.05, 1);
  }, { passive: true });

  let visible = true, raf = null;
  new IntersectionObserver((en) => {
    visible = en[0].isIntersecting;
    if (visible && raf === null) raf = requestAnimationFrame(frame);
  }).observe(canvas);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && visible && raf === null) raf = requestAnimationFrame(frame);
  });

  const t0 = performance.now();
  function frame(now) {
    raf = null;
    if (!visible || document.hidden) return;
    size();
    px += (tx - px) * 0.06; py += (ty - py) * 0.06;
    energy *= 0.965;
    gl.uniform2f(uRes, canvas.width, canvas.height);
    gl.uniform1f(uTime, (now - t0) / 1000);
    gl.uniform2f(uPtr, px, py);
    gl.uniform1f(uPtrV, energy);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    raf = requestAnimationFrame(frame);
  }
  raf = requestAnimationFrame(frame);
})();
